import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spin, Alert, Typography } from 'antd';
import { DownloadOutlined, SoundOutlined } from '@ant-design/icons';
import { decodeAudioToPlayableUrl } from '../utils/audioDecoder';

const { Text } = Typography;

const RecordingPlayerModal = ({
    open,
    onClose,
    recordingUrl,
    downloadUrl,
    title = "Reproducir Grabación"
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [audioBlobUrl, setAudioBlobUrl] = useState(null);
    const blobRef = useRef(null);

    // Limpieza de ObjectURL para evitar memory leaks
    const cleanupBlob = () => {
        if (blobRef.current) {
            URL.revokeObjectURL(blobRef.current);
            blobRef.current = null;
        }
        setAudioBlobUrl(null);
    };

    useEffect(() => {
        let isMounted = true;

        if (open && recordingUrl) {
            setLoading(true);
            setError(null);
            cleanupBlob();

            fetch(recordingUrl)
                .then(async (response) => {
                    if (!response.ok) {
                        const errText = await response.text().catch(() => '');
                        throw new Error(errText || `Error del servidor (${response.status})`);
                    }
                    return response.arrayBuffer();
                })
                .then(async (arrayBuffer) => {
                    if (!isMounted) return;
                    const { blobUrl } = await decodeAudioToPlayableUrl(arrayBuffer);
                    if (!isMounted) {
                        URL.revokeObjectURL(blobUrl);
                        return;
                    }
                    blobRef.current = blobUrl;
                    setAudioBlobUrl(blobUrl);
                    setLoading(false);
                })
                .catch((err) => {
                    if (!isMounted) return;
                    console.error("Error al cargar/decodificar audio:", err);
                    setError(err.message || 'No se pudo decodificar el archivo de grabación.');
                    setLoading(false);
                });
        } else {
            cleanupBlob();
            setError(null);
            setLoading(false);
        }

        return () => {
            isMounted = false;
            cleanupBlob();
        };
    }, [open, recordingUrl]);

    const handleCancel = () => {
        cleanupBlob();
        if (onClose) onClose();
    };

    return (
        <Modal
            title={
                <span>
                    <SoundOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    {title}
                </span>
            }
            open={open}
            onCancel={handleCancel}
            destroyOnClose
            footer={[
                <Button key="close" onClick={handleCancel}>
                    Cerrar
                </Button>,
                downloadUrl && (
                    <Button 
                        key="download" 
                        type="primary" 
                        icon={<DownloadOutlined />}
                        href={downloadUrl}
                        target="_blank"
                    >
                        Descargar
                    </Button>
                )
            ]}
        >
            <div style={{ padding: '24px 0', textAlign: 'center', minHeight: 100 }}>
                {loading && (
                    <div>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">Descargando y decodificando audio...</Text>
                        </div>
                    </div>
                )}

                {error && !loading && (
                    <Alert
                        type="error"
                        showIcon
                        message="Error de reproducción"
                        description={error}
                        style={{ textAlign: 'left' }}
                    />
                )}

                {audioBlobUrl && !loading && !error && (
                    <div>
                        <audio 
                            controls 
                            autoPlay 
                            src={audioBlobUrl} 
                            style={{ width: '100%', outline: 'none' }}
                        >
                            Tu navegador no soporta el elemento de audio.
                        </audio>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default RecordingPlayerModal;
